import { useEffect, useState, useCallback } from "react";
import {
  Button,
  Container,
  SimpleGrid,
  Paper,
  Text,
  Group,
  Switch,
  Table,
  Pagination,
  Select,
  NumberFormatter,
  LoadingOverlay,
  Stack,
  Badge,
} from "@mantine/core";
import { BarChart } from "@mantine/charts";
import { notifications } from "@mantine/notifications";
import {
  getAdminMonitoringSummary,
  getAdminDailyCosts,
  getAdminTenantCosts,
  getAdminRecentQueries,
  getAdminOverview,
  updateAdminMonitoringSettings,
  jsonPost,
} from "../api";
import { IconDatabase } from "@tabler/icons-react";
import GlobalSourcesPanel from "./GlobalSourcesPanel";

function StatCard({ label, value }) {
  return (
    <Paper withBorder p="md" radius="md">
      <Text c="dimmed" fz="sm" tt="uppercase" fw={700}>
        {label}
      </Text>
      <Text fz="xl" fw={700} mt="xs">
        {value}
      </Text>
    </Paper>
  );
}

export default function AdminMonitoringDashboard() {
  const [summary, setSummary] = useState(null);
  const [overview, setOverview] = useState(null);
  const [dailyCosts, setDailyCosts] = useState([]);
  const [tenantCosts, setTenantCosts] = useState([]);
  const [recentQueries, setRecentQueries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [costSourceFilter, setCostSourceFilter] = useState(null);
  const [togglingMonitoring, setTogglingMonitoring] = useState(false);
  const [togglingContent, setTogglingContent] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [summaryData, dailyData, tenantData, queriesData, overviewData] =
        await Promise.all([
          getAdminMonitoringSummary(),
          getAdminDailyCosts(),
          getAdminTenantCosts(),
          getAdminRecentQueries(page, 50, null, costSourceFilter),
          getAdminOverview(),
        ]);
      setSummary(summaryData);
      setOverview(overviewData);
      setDailyCosts(dailyData);
      setTenantCosts(tenantData);
      setRecentQueries(queriesData);
    } catch (err) {
      notifications.show({
        title: "Failed to load monitoring data",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  }, [page, costSourceFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggleMonitoring = async (checked) => {
    setTogglingMonitoring(true);
    try {
      await updateAdminMonitoringSettings({
        monitoringEnabled: checked,
        contentGenerationEnabled: summary.contentGenerationEnabled,
      });
      setSummary((prev) => ({ ...prev, monitoringEnabled: checked }));
      notifications.show({
        title: `Monitoring ${checked ? "enabled" : "disabled"}`,
        color: "teal",
      });
    } catch {
      notifications.show({ title: "Failed to update setting", color: "red" });
    } finally {
      setTogglingMonitoring(false);
    }
  };

  const handleToggleContent = async (checked) => {
    setTogglingContent(true);
    try {
      await updateAdminMonitoringSettings({
        monitoringEnabled: summary.monitoringEnabled,
        contentGenerationEnabled: checked,
      });
      setSummary((prev) => ({
        ...prev,
        contentGenerationEnabled: checked,
      }));
      notifications.show({
        title: `Content generation ${checked ? "enabled" : "disabled"}`,
        color: "teal",
      });
    } catch {
      notifications.show({ title: "Failed to update setting", color: "red" });
    } finally {
      setTogglingContent(false);
    }
  };

  if (loading) {
    return (
      <Container size="lg" py="xl" pos="relative" mih={400}>
        <LoadingOverlay visible />
      </Container>
    );
  }

  const totalPages = recentQueries
    ? Math.ceil(recentQueries.totalCount / recentQueries.pageSize)
    : 1;

  return (
    <Container size="lg" py="xl">
      <Text fz="xl" fw={700} mb="lg">
        Admin Dashboard
      </Text>

      {/* All Brands */}
      <Paper withBorder p="md" radius="md" mb="lg">
        <Text fw={600} mb="sm">
          All Brands
        </Text>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Website</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Tenant(s)</Table.Th>
              <Table.Th>Created</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {overview?.brands?.map((b) => (
              <Table.Tr key={b.id}>
                <Table.Td fw={500}>{b.name}</Table.Td>
                <Table.Td>{b.websiteUrl || "—"}</Table.Td>
                <Table.Td>
                  <Badge
                    size="sm"
                    variant="light"
                    color={
                      b.setupStatus === "Active"
                        ? "green"
                        : b.setupStatus === "MonitorSetup"
                          ? "yellow"
                          : "gray"
                    }
                  >
                    {b.setupStatus?.replace(/([A-Z])/g, " $1").trim()}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  {b.tenants?.map((t) => (
                    <Badge
                      key={t.tenantId}
                      size="xs"
                      variant="dot"
                      mr={4}
                      mb={2}
                    >
                      {t.tenantName} ({t.relationshipType})
                    </Badge>
                  ))}
                </Table.Td>
                <Table.Td>
                  {new Date(b.createdAt).toLocaleDateString()}
                </Table.Td>
              </Table.Tr>
            ))}
            {(!overview?.brands || overview.brands.length === 0) && (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text c="dimmed" ta="center">
                    No brands
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Active Monitors */}
      <Paper withBorder p="md" radius="md" mb="lg">
        <Text fw={600} mb="sm">
          Active Monitors
        </Text>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Brand</Table.Th>
              <Table.Th>Tenant</Table.Th>
              <Table.Th ta="right">Frequency</Table.Th>
              <Table.Th>Last Run</Table.Th>
              <Table.Th>Next Run</Table.Th>
              <Table.Th>Search</Table.Th>
              <Table.Th ta="right">Providers</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {overview?.activeMonitors?.map((m) => (
              <Table.Tr key={m.id}>
                <Table.Td fw={500}>{m.brandName}</Table.Td>
                <Table.Td>{m.tenantName}</Table.Td>
                <Table.Td ta="right">{m.frequencyInDays}d</Table.Td>
                <Table.Td>
                  {m.lastRun
                    ? new Date(m.lastRun).toLocaleString(undefined, {
                        dateStyle: "short",
                        timeStyle: "short",
                      })
                    : "Never"}
                </Table.Td>
                <Table.Td>
                  {m.nextRun
                    ? new Date(m.nextRun).toLocaleString(undefined, {
                        dateStyle: "short",
                        timeStyle: "short",
                      })
                    : "—"}
                </Table.Td>
                <Table.Td>
                  {m.useSearch ? (
                    <Badge size="xs" color="teal">
                      Yes
                    </Badge>
                  ) : (
                    "No"
                  )}
                </Table.Td>
                <Table.Td ta="right">{m.llmProviderCount}</Table.Td>
              </Table.Tr>
            ))}
            {(!overview?.activeMonitors ||
              overview.activeMonitors.length === 0) && (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text c="dimmed" ta="center">
                    No active monitors
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Recent Runs */}
      <Paper withBorder p="md" radius="md" mb="lg">
        <Text fw={600} mb="sm">
          Recent Monitor Runs
        </Text>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Brand</Table.Th>
              <Table.Th>Tenant</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Started</Table.Th>
              <Table.Th ta="right">Duration</Table.Th>
              <Table.Th ta="right">Queries</Table.Th>
              <Table.Th ta="right">Cost</Table.Th>
              <Table.Th>Error</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {overview?.recentRuns?.map((r) => (
              <Table.Tr key={r.id}>
                <Table.Td fw={500}>{r.brandName}</Table.Td>
                <Table.Td>{r.tenantName}</Table.Td>
                <Table.Td>
                  <Badge
                    size="sm"
                    variant="light"
                    color={
                      r.status === "Completed"
                        ? "green"
                        : r.status === "Failed"
                          ? "red"
                          : "blue"
                    }
                  >
                    {r.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  {new Date(r.startTimeUtc).toLocaleString(undefined, {
                    dateStyle: "short",
                    timeStyle: "short",
                  })}
                </Table.Td>
                <Table.Td ta="right">
                  {r.endTimeUtc
                    ? `${Math.round((new Date(r.endTimeUtc) - new Date(r.startTimeUtc)) / 1000)}s`
                    : "—"}
                </Table.Td>
                <Table.Td ta="right">{r.totalQueriesFinished}</Table.Td>
                <Table.Td ta="right">${(r.costUsd ?? 0).toFixed(4)}</Table.Td>
                <Table.Td>
                  {r.errorMessage ? (
                    <Text c="red" fz="xs" lineClamp={1}>
                      {r.errorMessage}
                    </Text>
                  ) : null}
                </Table.Td>
              </Table.Tr>
            ))}
            {(!overview?.recentRuns || overview.recentRuns.length === 0) && (
              <Table.Tr>
                <Table.Td colSpan={8}>
                  <Text c="dimmed" ta="center">
                    No runs
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Content & Events per Brand */}
      <Paper withBorder p="md" radius="md" mb="lg">
        <Text fw={600} mb="sm">
          Content & Events per Brand
        </Text>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Brand</Table.Th>
              <Table.Th>Tenant</Table.Th>
              <Table.Th ta="right">Content Items</Table.Th>
              <Table.Th ta="right">Tracking Events</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {overview?.brandStats?.map((s, i) => (
              <Table.Tr key={i}>
                <Table.Td fw={500}>{s.brandName}</Table.Td>
                <Table.Td>{s.tenantName}</Table.Td>
                <Table.Td ta="right">
                  <NumberFormatter
                    value={s.contentItemCount}
                    thousandSeparator
                  />
                </Table.Td>
                <Table.Td ta="right">
                  <NumberFormatter
                    value={s.trackingEventCount}
                    thousandSeparator
                  />
                </Table.Td>
              </Table.Tr>
            ))}
            {(!overview?.brandStats || overview.brandStats.length === 0) && (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text c="dimmed" ta="center">
                    No data
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Summary Cards */}
      <SimpleGrid cols={{ base: 1, sm: 3 }} mb="lg">
        <StatCard
          label="Total Cost (30d)"
          value={`$${(summary?.totalCostUsd ?? 0).toFixed(4)}`}
        />
        <StatCard
          label="Total Queries (30d)"
          value={
            <NumberFormatter
              value={summary?.totalQueries ?? 0}
              thousandSeparator
            />
          }
        />
        <StatCard
          label="Active Monitors"
          value={summary?.activeMonitors ?? 0}
        />
      </SimpleGrid>

      {/* Kill Switches */}
      <Paper withBorder p="md" radius="md" mb="lg">
        <Text fw={600} mb="sm">
          Kill Switches
        </Text>
        <Stack gap="sm">
          <Group justify="space-between">
            <Text>LLM Monitoring</Text>
            <Switch
              checked={summary?.monitoringEnabled ?? false}
              onChange={(e) => handleToggleMonitoring(e.currentTarget.checked)}
              disabled={togglingMonitoring}
            />
          </Group>
          <Group justify="space-between">
            <Text>Content Generation</Text>
            <Switch
              checked={summary?.contentGenerationEnabled ?? false}
              onChange={(e) => handleToggleContent(e.currentTarget.checked)}
              disabled={togglingContent}
            />
          </Group>
        </Stack>
      </Paper>

      {/* Seed & Tools */}
      <Paper withBorder p="md" radius="md" mb="lg">
        <Text fw={600} mb="sm">
          Seed & Tools
        </Text>
        <Group>
          <Button
            variant="light"
            leftSection={<IconDatabase size={16} />}
            loading={seeding}
            onClick={async () => {
              setSeeding(true);
              try {
                const result = await jsonPost("admin/seed-demo/reset");
                notifications.show({
                  title: "Demo reset & seeded",
                  message: `${result.brandName}: ${result.analysisRecords} analysis records, ${result.contentItems} articles, ${result.trackingEvents} traffic events`,
                  color: "green",
                });
                loadData();
              } catch {
                notifications.show({ title: "Seed failed", color: "red" });
              } finally {
                setSeeding(false);
              }
            }}
          >
            Reset & Seed TrailPeak
          </Button>
        </Group>
        <Text size="xs" c="dimmed" mt="xs">
          Deletes any existing TrailPeak brand, then creates a fresh demo with 4 weeks of monitoring data, content plan, articles, and traffic.
        </Text>
      </Paper>

      {/* Daily Cost Chart */}
      <Paper withBorder p="md" radius="md" mb="lg">
        <Text fw={600} mb="sm">
          Daily Costs (USD)
        </Text>
        {dailyCosts.length > 0 ? (
          <BarChart
            h={300}
            data={dailyCosts}
            dataKey="date"
            type="stacked"
            series={[
              { name: "monitoringCostUsd", label: "Monitoring", color: "blue" },
              {
                name: "contentCostUsd",
                label: "Content Generation",
                color: "orange",
              },
              {
                name: "imageGenCostUsd",
                label: "Image Generation",
                color: "green",
              },
              { name: "otherCostUsd", label: "Other", color: "gray" },
            ]}
            tickLine="y"
          />
        ) : (
          <Text c="dimmed" ta="center" py="xl">
            No cost data available yet.
          </Text>
        )}
      </Paper>

      {/* Cost by Tenant */}
      <Paper withBorder p="md" radius="md" mb="lg">
        <Text fw={600} mb="sm">
          Cost by Tenant (30d)
        </Text>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Tenant</Table.Th>
              <Table.Th ta="right">Queries</Table.Th>
              <Table.Th ta="right">USD Cost</Table.Th>
              <Table.Th ta="right">Brands</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {tenantCosts.map((tc) => (
              <Table.Tr key={tc.tenantId}>
                <Table.Td>{tc.tenantName}</Table.Td>
                <Table.Td ta="right">
                  <NumberFormatter value={tc.queryCount} thousandSeparator />
                </Table.Td>
                <Table.Td ta="right">${tc.costUsd.toFixed(4)}</Table.Td>
                <Table.Td ta="right">{tc.brandCount}</Table.Td>
              </Table.Tr>
            ))}
            {tenantCosts.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text c="dimmed" ta="center">
                    No data
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Recent Queries */}
      <Paper withBorder p="md" radius="md">
        <Group justify="space-between" mb="sm">
          <Text fw={600}>Recent Queries</Text>
          <Select
            placeholder="All sources"
            clearable
            w={200}
            value={costSourceFilter}
            onChange={(val) => {
              setCostSourceFilter(val);
              setPage(1);
            }}
            data={[
              { value: "Monitoring", label: "Monitoring" },
              { value: "ContentGeneration", label: "Content Generation" },
              { value: "ImageGeneration", label: "Image Generation" },
            ]}
          />
        </Group>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Time</Table.Th>
              <Table.Th>Model</Table.Th>
              <Table.Th ta="right">$Cost</Table.Th>
              <Table.Th>Source</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {recentQueries?.data?.map((q) => (
              <Table.Tr key={q.id}>
                <Table.Td>
                  {new Date(q.idCreated).toLocaleString(undefined, {
                    dateStyle: "short",
                    timeStyle: "short",
                  })}
                </Table.Td>
                <Table.Td>{q.modelName}</Table.Td>
                <Table.Td ta="right">${(q.costUsd ?? 0).toFixed(4)}</Table.Td>
                <Table.Td>
                  <Badge
                    size="sm"
                    variant="light"
                    color={
                      q.costSource === "Monitoring"
                        ? "blue"
                        : q.costSource?.startsWith("ImageGeneration")
                          ? "green"
                          : "orange"
                    }
                  >
                    {q.costSource ?? "Unknown"}
                  </Badge>
                </Table.Td>
              </Table.Tr>
            ))}
            {(!recentQueries?.data || recentQueries.data.length === 0) && (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text c="dimmed" ta="center">
                    No queries
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
        {totalPages > 1 && (
          <Group justify="center" mt="md">
            <Pagination value={page} onChange={setPage} total={totalPages} />
          </Group>
        )}
      </Paper>

      <GlobalSourcesPanel />
    </Container>
  );
}
